import React, { useState, useMemo, useRef, useEffect } from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Spinner, Empty } from "@/components/Atoms";
import { fmtRel } from "@/lib/format";
import {
    UploadCloud, Trash2, Rocket, Database, HardDrive, Globe2,
    Boxes, AlertTriangle, CheckCircle2, Loader2, X, Check, Mail,
} from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const fmtBytes = (n) => {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const STATUS_META = {
    uploading: { label: "Uploading", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: Loader2, spin: true },
    parsing: { label: "Parsing", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: Loader2, spin: true },
    parsed: { label: "Ready to deploy", cls: "bg-cm-brand/15 text-cm-brand border-cm-brand/30", icon: CheckCircle2 },
    deploying: { label: "Deploying", cls: "bg-teal-500/15 text-teal-300 border-teal-500/30", icon: Loader2, spin: true },
    deployed: { label: "Deployed", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: CheckCircle2 },
    error: { label: "Error", cls: "bg-red-500/15 text-red-300 border-red-500/30", icon: AlertTriangle },
};

function StatusPill({ status }) {
    const m = STATUS_META[status] || STATUS_META.parsing;
    const Icon = m.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${m.cls}`}
            data-testid={`import-status-${status}`}>
            <Icon size={12} className={m.spin ? "animate-spin" : ""} /> {m.label}
        </span>
    );
}

function MetaGrid({ meta, kind }) {
    if (!meta) return null;
    const db = meta.db || {};
    const s3 = meta.s3 || {};
    const apps = meta.custom_apps || meta.apps || [];
    const isSouvera = kind === "souvera";
    const Item = ({ icon: Icon, label, children }) => (
        <div className="flex items-start gap-2">
            <Icon size={14} className="mt-0.5 text-cm-muted shrink-0" />
            <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-cm-muted">{label}</div>
                <div className="text-sm text-cm-text break-words">{children}</div>
            </div>
        </div>
    );
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <Item icon={Globe2} label="Primary domain / NC version">
                <span data-testid="import-meta-domain">{meta.primary_domain || "—"}</span>
                <span className="text-cm-muted"> · NC {meta.nc_version || "?"}</span>
            </Item>
            {isSouvera ? (
                <Item icon={Mail} label="Mail domain / Stalwart">
                    <span data-testid="import-meta-mail">{(meta.mail_domains || [])[0] || "—"}</span>
                    <span className="text-cm-muted"> · Stalwart {meta.stalwart_version || "?"}</span>
                </Item>
            ) : (
                <Item icon={Database} label="Database (from backup)">
                    <span data-testid="import-meta-db">{db.name || "—"}</span>
                    <span className="text-cm-muted"> @ {db.host || "?"}</span>
                </Item>
            )}
            {isSouvera ? (
                <Item icon={HardDrive} label="Data migration (zero data loss)">
                    <span className="text-xs text-cm-muted" data-testid="import-meta-copy">
                        NEW NC + Stalwart stores are created, source is copied read-only.
                    </span>
                </Item>
            ) : (
                <Item icon={HardDrive} label="S3 bucket (MinIO→Garage :3900)">
                    <span className="font-mono text-xs" data-testid="import-meta-bucket">{s3.bucket || "—"}</span>
                    <div className="text-cm-muted text-xs">
                        {s3.hostname}:{s3.old_port} → :{s3.new_port}
                    </div>
                </Item>
            )}
            <Item icon={Boxes} label={`${isSouvera ? "Apps in backup" : "Custom Apps"} (${apps.length})`}>
                <span className="text-xs text-cm-muted" data-testid="import-meta-apps">
                    {apps.length ? apps.join(", ") : "—"}
                </span>
            </Item>
        </div>
    );
}

function ConflictBadges({ conflicts }) {
    if (!conflicts) return null;
    const hard = conflicts.hard || [];
    const warn = conflicts.warn || [];
    if (!hard.length && !warn.length) return null;
    return (
        <div className="mt-3 space-y-1.5" data-testid="import-conflicts">
            {hard.map((c, i) => (
                <div key={`h${i}`} className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-1.5">
                    <AlertTriangle size={14} className="shrink-0" /> {c}
                </div>
            ))}
            {warn.map((c, i) => (
                <div key={`w${i}`} className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-1.5">
                    <AlertTriangle size={14} className="shrink-0" /> {c}
                </div>
            ))}
        </div>
    );
}

export default function BackupImportPage() {
    const { data, mutate, isLoading } = useSWR("/api/admin/imports", fetcher, {
        refreshInterval: (latest) =>
            (latest?.imports || []).some((i) =>
                ["uploading", "parsing", "deploying"].includes(i.status))
                ? 3000 : 0,
    });
    const { data: resellers, mutate: reloadResellers } =
        useSWR("/api/admin/resellers/options", fetcher, {
            revalidateOnMount: true,
            shouldRetryOnError: true,
            errorRetryInterval: 2000,
            errorRetryCount: 8,
        });

    const imports = useMemo(() => data?.imports || [], [data]);
    const summary = data?.summary || { total: 0, deployable: 0 };
    const resellerName = useMemo(() => {
        const m = {};
        (resellers || []).forEach((r) => (m[r.id] = r.name));
        return m;
    }, [resellers]);

    const fileInput = useRef(null);
    const [uploads, setUploads] = useState({}); // name -> {pct, error}
    const [globalErr, setGlobalErr] = useState(null);

    // Batch-deploy selection (only deployable parsed imports, no hard conflict)
    const deployable = useMemo(
        () => imports.filter(
            (i) => i.status === "parsed" && !i.cloud_id
                && !(i.conflicts?.hard || []).length),
        [imports]);
    const [selected, setSelected] = useState(() => new Set());
    const toggleSelect = (id) =>
        setSelected((s) => {
            const next = new Set(s);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const handleFiles = async (files) => {
        setGlobalErr(null);
        for (const file of Array.from(files)) {
            if (!file.name.toLowerCase().endsWith(".zip")) {
                setUploads((u) => ({ ...u, [file.name]: { pct: 0, error: "Not a .zip file" } }));
                continue;
            }
            try {
                await uploadOne(file);
            } catch (e) {
                setUploads((u) => ({ ...u, [file.name]: { pct: u[file.name]?.pct || 0, error: formatApiError(e) } }));
            }
        }
        mutate();
    };

    const uploadOne = async (file) => {
        setUploads((u) => ({ ...u, [file.name]: { pct: 0 } }));
        const init = await api.post("/api/admin/imports/upload/init", {
            filename: file.name, size: file.size,
        });
        const importId = init.data.import_id;
        const chunkSize = init.data.chunk_size || 8 * 1024 * 1024;
        let sent = 0;
        for (let off = 0; off < file.size; off += chunkSize) {
            const blob = file.slice(off, Math.min(off + chunkSize, file.size));
            const buf = await blob.arrayBuffer();
            await api.put(`/api/admin/imports/${importId}/chunk`, buf, {
                headers: { "Content-Type": "application/octet-stream" },
            });
            sent += blob.size;
            const pct = Math.round((sent / file.size) * 100);
            setUploads((u) => ({ ...u, [file.name]: { pct } }));
        }
        await api.post(`/api/admin/imports/${importId}/complete`);
        setUploads((u) => {
            const next = { ...u };
            delete next[file.name];
            return next;
        });
        mutate();
    };

    return (
        <div className="space-y-6" data-testid="backup-import-page">
            <PageHeader
                title="Cloud Import"
                subtitle="Upload backup ZIPs from the old Kubernetes stack — CM reads DB/S3 credentials + identity from them, restores config/custom_apps/themes, and deploys the cloud."
                testid="import-header"
            />

            {/* Upload zone */}
            <div
                className="border-2 border-dashed border-cm-line2 p-8 text-center hover:border-cm-brand/40 transition-colors cursor-pointer bg-cm-bg"
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                data-testid="import-dropzone"
            >
                <UploadCloud size={36} className="mx-auto text-cm-muted" />
                <div className="mt-3 text-cm-text font-medium">Drag backup ZIP(s) here or click to upload</div>
                <div className="text-sm text-cm-muted mt-1">Multiple files at once supported · large files are uploaded in chunks</div>
                <input
                    ref={fileInput} type="file" accept=".zip" multiple className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                    data-testid="import-file-input"
                />
            </div>

            {globalErr && <div className="text-sm text-red-300">{globalErr}</div>}

            {/* Active uploads */}
            {Object.keys(uploads).length > 0 && (
                <div className="space-y-2" data-testid="import-active-uploads">
                    {Object.entries(uploads).map(([name, st]) => (
                        <div key={name} className="bg-cm-surface2/40 border border-cm-line2 p-3">
                            <div className="flex justify-between text-sm">
                                <span className="text-cm-text truncate">{name}</span>
                                <span className={st.error ? "text-red-300" : "text-cm-muted"}>
                                    {st.error || `${st.pct}%`}
                                </span>
                            </div>
                            {!st.error && (
                                <div className="h-1.5 bg-cm-surface2 rounded-full mt-2 overflow-hidden">
                                    <div className="h-full bg-cm-brand transition-all" style={{ width: `${st.pct}%` }} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Batch deploy bar */}
            {deployable.length > 0 && (
                <BatchBar
                    deployable={deployable} selected={selected}
                    setSelected={setSelected} resellers={resellers || []}
                    onReloadResellers={reloadResellers} onDone={mutate}
                />
            )}

            {/* Imports list */}
            {isLoading ? (
                <Spinner label="Loading imports…" />
            ) : imports.length === 0 ? (
                <Empty title="No imports yet" hint="Upload a backup ZIP to get started." testid="import-empty" />
            ) : (
                <div className="space-y-4" data-testid="import-list">
                    {imports.map((imp) => {
                        const selectable = imp.status === "parsed" && !imp.cloud_id
                            && !(imp.conflicts?.hard || []).length;
                        return (
                            <ImportCard
                                key={imp.import_id} imp={imp}
                                resellers={resellers || []} resellerName={resellerName}
                                onReloadResellers={reloadResellers}
                                onChange={mutate}
                                selectable={selectable}
                                selected={selected.has(imp.import_id)}
                                onToggleSelect={() => toggleSelect(imp.import_id)}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function BatchBar({ deployable, selected, setSelected, resellers, onReloadResellers, onDone }) {
    const [resellerId, setResellerId] = useState("");
    const [storageGb, setStorageGb] = useState(100);
    const [overrides, setOverrides] = useState({});   // import_id -> {storage_gb, reseller_id}
    const [showOverrides, setShowOverrides] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [results, setResults] = useState(null);
    const [progress, setProgress] = useState(null);   // {done,total} during chunked deploy
    const noResellers = !resellers || resellers.length === 0;
    const ids = deployable.map((d) => d.import_id);
    const selCount = ids.filter((id) => selected.has(id)).length;
    const allSelected = selCount === ids.length && ids.length > 0;

    // Default to the first reseller so a batch can be deployed in one click.
    useEffect(() => {
        if (!resellerId && (resellers || []).length > 0) {
            setResellerId(resellers[0].id);
        }
    }, [resellers, resellerId]);

    const setOverride = (id, key, val) =>
        setOverrides((o) => ({ ...o, [id]: { ...(o[id] || {}), [key]: val } }));

    const selectAll = () =>
        setSelected(allSelected ? new Set() : new Set(ids));

    const submit = async () => {
        setErr(null); setResults(null); setProgress(null);
        if (!resellerId) { setErr("Please select a reseller."); return; }
        const items = ids.filter((id) => selected.has(id)).map((id) => {
            const o = overrides[id] || {};
            const it = { import_id: id };
            if (o.storage_gb) it.storage_gb = Number(o.storage_gb);
            if (o.reseller_id) it.reseller_id = o.reseller_id;
            return it;
        });
        if (!items.length) { setErr("No backups selected."); return; }
        setBusy(true);
        // Deploy in chunks so a large batch (e.g. 159 clouds) never hits the
        // ingress/HTTP timeout. Each request only ENQUEUES cloud.build jobs;
        // the job runner then drains the queue N-at-a-time (concurrency cap).
        const CHUNK = 25;
        const merged = { deployed: 0, total: 0, results: [] };
        try {
            for (let i = 0; i < items.length; i += CHUNK) {
                setProgress({ done: i, total: items.length });
                const slice = items.slice(i, i + CHUNK);
                const r = await api.post("/api/admin/imports/batch-deploy", {
                    reseller_id: resellerId, items: slice, storage_gb: Number(storageGb),
                });
                merged.deployed += r.data?.deployed || 0;
                merged.total += r.data?.total || 0;
                merged.results.push(...(r.data?.results || []));
            }
            setProgress({ done: items.length, total: items.length });
            setResults(merged);
            setSelected(new Set());
            onDone();
        } catch (e) {
            const extra = merged.results.length
                ? ` (${merged.deployed}/${merged.total} already queued)` : "";
            setErr(formatApiError(e) + extra);
            if (merged.results.length) setResults(merged);
            onDone();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-cm-brand/5 border border-cm-brand/25 p-4" data-testid="import-batch-bar">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-cm-text">
                    <Rocket size={16} className="text-cm-brand" />
                    <span className="font-medium">Batch deploy</span>
                    <span className="text-sm text-cm-muted">
                        {selCount} of {ids.length} ready selected
                    </span>
                    <button onClick={selectAll}
                        className="text-xs text-cm-brand hover:underline ml-1"
                        data-testid="import-batch-selectall">
                        {allSelected ? "Clear selection" : "Select all"}
                    </button>
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                    <label className="block">
                        <span className="text-xs uppercase tracking-wide text-cm-muted">Reseller (for all)</span>
                        <select value={resellerId} onChange={(e) => setResellerId(e.target.value)}
                            className="mt-1 bg-[#0b0e14] border border-cm-line2 px-3 py-2 text-sm text-cm-text min-w-[200px]"
                            data-testid="import-batch-reseller">
                            <option value="">{noResellers ? "— none loaded —" : "— select —"}</option>
                            {(resellers || []).map((r) => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-wide text-cm-muted">Storage (GB)</span>
                        <input type="number" min={10} value={storageGb}
                            onChange={(e) => setStorageGb(e.target.value)}
                            className="mt-1 w-24 bg-[#0b0e14] border border-cm-line2 px-3 py-2 text-sm text-cm-text"
                            data-testid="import-batch-storage" />
                    </label>
                    <button onClick={submit} disabled={busy || selCount === 0}
                        className="cm-btn-primary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
                        data-testid="import-batch-deploy-submit">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}
                        {busy && progress
                            ? `Queueing… ${progress.done}/${progress.total}`
                            : `Deploy ${selCount} cloud${selCount === 1 ? "" : "s"}`}
                    </button>
                </div>
            </div>
            {busy && progress && progress.total > 25 && (
                <div className="mt-3" data-testid="import-batch-progress">
                    <div className="h-1.5 rounded-full bg-cm-surface2 overflow-hidden">
                        <div className="h-full bg-cm-brand transition-all"
                            style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
                    </div>
                    <div className="text-[11px] text-cm-muted mt-1">
                        {progress.total} clouds are enqueued as jobs in batches of 25 — the job runner builds them throttled one after another.
                    </div>
                </div>
            )}
            {selCount > 0 && (
                <div className="mt-3 border-t border-cm-line2 pt-3" data-testid="import-batch-overrides">
                    <button type="button"
                        onClick={() => setShowOverrides((v) => !v)}
                        className="text-xs text-cm-brand hover:underline"
                        data-testid="import-batch-overrides-toggle">
                        Per-cloud overrides ({selCount}) {showOverrides ? "hide ▲" : "show ▼"}
                    </button>
                    {showOverrides && (
                        <div className="mt-2 space-y-1.5">
                            <div className="text-[11px] text-cm-muted">
                                Leave empty = global default ({storageGb} GB · {(resellers || []).find((r) => r.id === resellerId)?.name || "—"}).
                                Detected services are activated automatically after deploy.
                            </div>
                            {deployable.filter((d) => selected.has(d.import_id)).map((d) => {
                                const o = overrides[d.import_id] || {};
                                const name = d.meta?.primary_domain || d.original_filename || d.import_id.slice(0, 8);
                                const svc = d.meta?.service_apps || [];
                                return (
                                    <div key={d.import_id}
                                        className="flex items-center gap-2 flex-wrap bg-[#0b0e14] border border-cm-line2 px-3 py-2"
                                        data-testid={`import-batch-override-${d.import_id}`}>
                                        <span className="text-xs text-cm-text font-mono truncate flex-1 min-w-[160px]">{name}</span>
                                        {svc.length > 0 && (
                                            <span className="text-[11px] text-teal-300/80">{svc.join(", ")}</span>
                                        )}
                                        <input type="number" min={10} placeholder={`${storageGb} GB`}
                                            value={o.storage_gb || ""}
                                            onChange={(e) => setOverride(d.import_id, "storage_gb", e.target.value)}
                                            className="w-24 bg-[#0b0e14] border border-cm-line2 px-2 py-1 text-xs text-cm-text"
                                            data-testid={`import-batch-override-storage-${d.import_id}`} />
                                        <select value={o.reseller_id || ""}
                                            onChange={(e) => setOverride(d.import_id, "reseller_id", e.target.value)}
                                            className="bg-[#0b0e14] border border-cm-line2 px-2 py-1 text-xs text-cm-text min-w-[140px]"
                                            data-testid={`import-batch-override-reseller-${d.import_id}`}>
                                            <option value="">— global —</option>
                                            {(resellers || []).map((r) => (
                                                <option key={r.id} value={r.id}>{r.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
            {noResellers && (
                <button type="button" onClick={() => onReloadResellers && onReloadResellers()}
                    className="mt-1 text-xs text-cm-brand hover:underline"
                    data-testid="import-batch-reload-resellers">
                    Reload reseller list
                </button>
            )}
            {err && <div className="mt-2 text-sm text-red-300">{err}</div>}
            {results && (
                <div className="mt-3 text-sm space-y-1" data-testid="import-batch-results">
                    <div className="text-cm-text">{results.deployed} of {results.total} deployed:</div>
                    {results.results.map((r) => (
                        <div key={r.import_id} className={r.ok ? "text-teal-300" : "text-red-300"}>
                            {r.ok ? "✓" : "✗"} {r.import_id.slice(0, 8)} {r.ok ? `→ ${r.fallback_hostname || r.cloud_id}` : `— ${r.error}`}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ImportCard({ imp, resellers, resellerName, onReloadResellers, onChange, selectable, selected, onToggleSelect }) {
    const [showDeploy, setShowDeploy] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const hasHardConflict = (imp.conflicts?.hard || []).length > 0;

    const remove = async () => {
        if (!window.confirm("Delete this import (including the uploaded bundle)?")) return;
        setBusy(true);
        try {
            await api.delete(`/api/admin/imports/${imp.import_id}`);
            onChange();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={`bg-cm-surface2/40 border p-4 transition-colors ${selected ? "border-cm-brand/50 bg-cm-brand/5" : "border-cm-line2"}`} data-testid={`import-card-${imp.import_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex items-start gap-3">
                    {selectable && (
                        <input
                            type="checkbox"
                            checked={selected}
                            onChange={onToggleSelect}
                            className="mt-1 h-4 w-4 shrink-0 accent-[#CCFF00] cursor-pointer"
                            aria-label="Select for batch deploy"
                            data-testid={`import-select-${imp.import_id}`}
                        />
                    )}
                    <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-cm-text truncate">{imp.original_filename || imp.import_id}</span>
                        <StatusPill status={imp.status} />
                        {imp.kind === "souvera" && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-teal-400/30 bg-teal-400/15 text-teal-200 text-xs font-medium"
                                data-testid={`import-souvera-badge-${imp.import_id}`}>
                                <Mail size={11} /> Souvera (NC + Stalwart)
                            </span>
                        )}
                        {hasHardConflict && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-red-500/30 bg-red-500/15 text-red-300 text-xs font-medium"
                                data-testid={`import-conflict-badge-${imp.import_id}`}>
                                <AlertTriangle size={11} /> Conflict
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-cm-muted mt-1">
                        {fmtBytes(imp.bundle_size || imp.expected_size)} · {fmtRel(imp.created_at)}
                        {imp.backup_namespace && <span className="font-mono"> · ns {imp.backup_namespace.slice(0, 8)}</span>}
                    </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {imp.status === "parsed" && (
                        <button
                            onClick={() => setShowDeploy((s) => !s)}
                            className="cm-btn-primary inline-flex items-center gap-1.5 text-sm"
                            data-testid={`import-deploy-toggle-${imp.import_id}`}
                        >
                            <Rocket size={14} /> Deploy
                        </button>
                    )}
                    {imp.cloud_id && (
                        <Link to={`/clouds/${imp.cloud_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-cm-line2 hover:bg-cm-surface2 text-cm-text text-sm transition-colors"
                            data-testid={`import-view-cloud-${imp.import_id}`}>
                            <Globe2 size={14} /> Open cloud
                        </Link>
                    )}
                    {imp.status !== "deploying" && (
                        <button onClick={remove} disabled={busy}
                            className="p-2 border border-cm-line2 hover:border-red-500/40 hover:text-red-300 text-cm-muted transition-colors"
                            data-testid={`import-delete-${imp.import_id}`}>
                            <Trash2 size={15} />
                        </button>
                    )}
                </div>
            </div>

            {imp.status === "error" && imp.error && (
                <div className="mt-2 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                    {imp.error}
                </div>
            )}

            {imp.meta && <MetaGrid meta={imp.meta} kind={imp.kind} />}

            <ConflictBadges conflicts={imp.conflicts} />

            {err && <div className="mt-2 text-sm text-red-300">{err}</div>}

            {showDeploy && imp.status === "parsed" && (
                <DeployForm imp={imp} resellers={resellers}
                    onReloadResellers={onReloadResellers}
                    onClose={() => setShowDeploy(false)} onDone={onChange} />
            )}
        </div>
    );
}

function DeployForm({ imp, resellers, onReloadResellers, onClose, onDone }) {
    const [resellerId, setResellerId] = useState("");
    // Default the cloud name to the ORIGINAL Nextcloud instanceid (read from the
    // backup) — stable + uniquely identifies the source instance. Operators can
    // override, or leave blank → backend falls back to the instanceid too.
    const importUuid = imp.meta?.instanceid || "";
    const [name, setName] = useState(importUuid);
    const [storageGb, setStorageGb] = useState(100);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const noResellers = !resellers || resellers.length === 0;

    // Service activation is OPERATOR-CONTROLLED — never auto-enabled from the
    // mere presence of an app folder in the backup. Only show toggles for
    // services whose app is actually present; default all OFF. notify_push is
    // NOT listed — it ships with every cloud and is wired automatically.
    const SERVICE_OPTS = [
        { app: "richdocuments", service: "collabora", label: "Collabora (Office)" },
        { app: "spreed", service: "talk-hpb", label: "Talk HPB" },
        { app: "whiteboard", service: "whiteboard", label: "Whiteboard" },
    ];
    const availableServices = SERVICE_OPTS.filter(
        (o) => (imp.meta?.service_apps || []).includes(o.app));
    const [enableServices, setEnableServices] = useState([]);
    const toggleService = (svc) => setEnableServices((cur) =>
        cur.includes(svc) ? cur.filter((s) => s !== svc) : [...cur, svc]);

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        if (!resellerId) { setErr("Please select a reseller."); return; }
        setBusy(true);
        try {
            await api.post(`/api/admin/imports/${imp.import_id}/deploy`, {
                reseller_id: resellerId, name: name.trim() || undefined,
                storage_gb: Number(storageGb),
                enable_services: enableServices,
            });
            onDone();
            onClose();
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit}
            className="mt-4 pt-4 border-t border-cm-line2 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end"
            data-testid={`import-deploy-form-${imp.import_id}`}>
            {imp.kind === "souvera" && (
                <div className="sm:col-span-3 text-xs text-teal-200/90 bg-teal-400/10 border border-teal-400/20 rounded-lg px-3 py-2"
                    data-testid={`import-souvera-note-${imp.import_id}`}>
                    <b>Full Souvera import (orchestrated, zero data loss):</b> NEW
                    NC + Stalwart stores are created and the source is copied in read-only.
                    Sequence: data copy → NC import on source version → remove old apps →
                    NC major update → Stalwart deploy + migration → new Souvera apps →
                    groups/SCADMIN + user licensing.
                </div>
            )}
            <label className="block">
                <span className="text-xs uppercase tracking-wide text-cm-muted">Reseller</span>
                <select value={resellerId} onChange={(e) => setResellerId(e.target.value)}
                    className="mt-1 w-full bg-[#0b0e14] border border-cm-line2 px-3 py-2 text-sm text-cm-text"
                    data-testid={`import-deploy-reseller-${imp.import_id}`}>
                    <option value="">{noResellers ? "— none loaded —" : "— select —"}</option>
                    {(resellers || []).map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                </select>
                {noResellers && (
                    <button type="button"
                        onClick={() => onReloadResellers && onReloadResellers()}
                        className="mt-1 text-xs text-cm-brand hover:underline"
                        data-testid={`import-deploy-reload-resellers-${imp.import_id}`}>
                        Reload reseller list
                    </button>
                )}
            </label>
            <label className="block">
                <span className="text-xs uppercase tracking-wide text-cm-muted">Cloud name <span className="text-cm-dim normal-case">(optional — default: import UUID)</span></span>
                <input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder={importUuid || "Import UUID (automatic)"}
                    className="mt-1 w-full bg-[#0b0e14] border border-cm-line2 px-3 py-2 text-sm text-cm-text font-mono"
                    data-testid={`import-deploy-name-${imp.import_id}`} />
            </label>
            <label className="block">
                <span className="text-xs uppercase tracking-wide text-cm-muted">Storage (GB)</span>
                <input type="number" min={10} value={storageGb}
                    onChange={(e) => setStorageGb(e.target.value)}
                    className="mt-1 w-full bg-[#0b0e14] border border-cm-line2 px-3 py-2 text-sm text-cm-text"
                    data-testid={`import-deploy-storage-${imp.import_id}`} />
            </label>
            {availableServices.length > 0 && (
                <div className="sm:col-span-3"
                    data-testid={`import-deploy-services-${imp.import_id}`}>
                    <span className="text-xs uppercase tracking-wide text-cm-muted">
                        Enable services <span className="text-cm-dim normal-case">(optional — off by default; only those detected in the backup)</span>
                    </span>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                        {availableServices.map((o) => {
                            const on = enableServices.includes(o.service);
                            return (
                                <button key={o.service} type="button"
                                    onClick={() => toggleService(o.service)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-sm transition-colors ${on ? "bg-cm-brand/20 border-cm-brand/60 text-cm-text" : "bg-[#0b0e14] border-cm-line2 text-cm-muted hover:bg-cm-surface2"}`}
                                    data-testid={`import-deploy-service-${o.service}-${imp.import_id}`}>
                                    <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${on ? "bg-cm-brand border-cm-brand" : "border-cm-line2"}`}>
                                        {on && <Check size={10} className="text-cm-bg" />}
                                    </span>
                                    {o.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
            <div className="sm:col-span-3 flex items-center gap-2">
                <button type="submit" disabled={busy}
                    className="cm-btn-primary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
                    data-testid={`import-deploy-submit-${imp.import_id}`}>
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}
                    {imp.meta?.nc_major ? `Deploy cloud (NC ${imp.meta.nc_major})` : "Deploy cloud"}
                </button>
                <button type="button" onClick={onClose}
                    className="inline-flex items-center gap-1 px-3 py-2 border border-cm-line2 text-cm-text text-sm hover:bg-cm-surface2">
                    <X size={14} /> Cancel
                </button>
                {err && <span className="text-sm text-red-300">{err}</span>}
            </div>
        </form>
    );
}
